import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api/rawFetch'

// Interfaces declared locally rather than pulled from generated.ts — same
// escape hatch as useStocktakes.ts / useProductRequests.ts. Mirrors
// backend/schemas/adjustment.py.

/** A manual one-off starts as a 'draft'; a stocktake apply files one already
 *  'performed'. There is no third state and 'performed' is terminal. */
export type AdjustmentStatus = 'draft' | 'performed'

/** Open = a draft still waiting to be pushed; archived = performed record. */
export const OPEN_ADJUSTMENT_STATUSES: AdjustmentStatus[] = ['draft']

export interface AdjustmentSummary {
  adjustment_id: number
  /** Set when a stocktake apply filed this record. */
  session_id: number | null
  status: string
  /** Blank for stocktake-generated records — matches Stocky. */
  reason: string
  /** "Stocktake #<n>" for stocktake-generated records. */
  notes: string
  location_id: number | null
  location_name: string
  created_by: string | null
  created_at: string | null
  adjusted_by: string | null
  adjusted_at: string | null
  apply_error: string | null
  /** The list badge. 0 is real: a count that found nothing still files a record. */
  variant_count: number
}

export interface AdjustmentLine {
  adj_line_id: number
  variant_id: number
  inventory_item_id: number | null
  sku: string
  product_title: string
  variant_title: string
  /** NULL on a draft — stamped at apply time from the live Shopify read. */
  old_qty: number | null
  /** Likewise NULL on a draft: new_qty − live on_hand isn't known until apply. */
  delta: number | null
  /** The TARGET available quantity, which is what a draft line carries. */
  new_qty: number | null
  apply_status: 'adjusted' | 'skipped_zero' | 'failed' | null
  apply_error: string | null
}

export interface AdjustmentDetail extends AdjustmentSummary {
  lines: AdjustmentLine[]
}

export interface AdjustmentCreatePayload {
  location_id: number
  reason?: string
  notes?: string
}

export interface AdjustmentApplyResult {
  ok: boolean
  adjustment_id: number
  adjusted: number
  skipped_zero: number
  error: string | null
}

export function useAdjustmentList(status?: string) {
  return useQuery({
    queryKey: ['adjustments', status ?? ''] as const,
    queryFn: () =>
      apiFetch<AdjustmentSummary[]>('GET', '/api/adjustments', { query: { status } }),
    staleTime: 30_000,
  })
}

export function useAdjustment(adjustmentId: string | null) {
  return useQuery({
    queryKey: ['adjustment', adjustmentId] as const,
    queryFn: () =>
      apiFetch<AdjustmentDetail>('GET', `/api/adjustments/${adjustmentId}`),
    enabled: !!adjustmentId,
  })
}

export function useCreateAdjustment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: AdjustmentCreatePayload) =>
      apiFetch<AdjustmentDetail>('POST', '/api/adjustments', { body: payload }),
    onSuccess: (detail) => {
      qc.setQueryData(['adjustment', String(detail.adjustment_id)], detail)
      qc.invalidateQueries({ queryKey: ['adjustments'] })
    },
  })
}

/** Set one variant's target quantity on a draft. Upserts per variant, so
 *  re-picking a variant already on the record edits it rather than duplicating
 *  it. 409 = no longer a draft; 404 = unknown variant. */
export function useUpsertAdjustmentLine(adjustmentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { variant_id: number; new_qty: number }) =>
      apiFetch<AdjustmentDetail>('POST', `/api/adjustments/${adjustmentId}/lines`, {
        body: vars,
      }),
    onSuccess: (detail) => {
      qc.setQueryData(['adjustment', adjustmentId], detail)
      qc.invalidateQueries({ queryKey: ['adjustments'] })
    },
  })
}

export function useDeleteAdjustmentLine(adjustmentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (lineId: number) =>
      apiFetch<AdjustmentDetail>(
        'DELETE',
        `/api/adjustments/${adjustmentId}/lines/${lineId}`
      ),
    onSuccess: (detail) => {
      qc.setQueryData(['adjustment', adjustmentId], detail)
      qc.invalidateQueries({ queryKey: ['adjustments'] })
    },
  })
}

/** Push the draft to Shopify.
 *
 *  NB: a Shopify refusal comes back as HTTP 200 with ok=false — callers must
 *  check `ok`, not just the absence of a thrown error. Same convention as
 *  useApplyStocktake. Either way the detail is invalidated: success flips the
 *  record to 'performed' and stamps old_qty/delta per line, failure leaves it a
 *  draft with apply_error set (and deltas recomputed on every attempt, so a
 *  retry can't double-apply). */
export function useApplyAdjustment(adjustmentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<AdjustmentApplyResult>('POST', `/api/adjustments/${adjustmentId}/apply`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adjustment', adjustmentId] })
      qc.invalidateQueries({ queryKey: ['adjustments'] })
    },
  })
}
